import React, {useEffect, useMemo, useState} from 'react';
import {gql} from '@apollo/client';
import {History} from '@mui/icons-material';
import {Alert, Box, Button, CircularProgress, Table, TableBody, TableCell, TableHead, TableRow, Typography} from '@mui/material';
import {useQuery} from '@apollo/client/react';
import dayjs from 'dayjs';
import {LatLngExpression} from 'leaflet';
import L from 'leaflet';
import {CircleMarker, Polyline, useMap} from 'react-leaflet';

import ActionMenuItem, {ActionMenuItemProps} from 'components/list/actionMenu/ActionMenuItem';
import {useRecordContext} from 'components/record/useRecordContext';
import CustomDialog from 'components/forms/CustomDialog';
import {DialogContextProvider, useDialogContext} from 'components/dialog/DialogContext';
import MapContainer from 'components/common/MapContainer';
import {getUserRecentActivities} from 'services/gqlQueries';

type ActivityProvider = 'GARMIN' | 'SUUNTO' | 'COROS';

type ProviderActivity = {
  id: number;
  externalId?: string | null;
  name?: string | null;
  activityType?: string | null;
  startTimeInSeconds: number;
  durationInSeconds?: number | null;
};

type TrackPoint = {lat?: number | null; lng?: number | null};

const MY_ACTIVITY = gql`
  query UserActivityTrack($provider: ActivityProvider!, $id: Int!, $userId: Int!) {
    item: myActivity(provider: $provider, id: $id, userId: $userId) {
      id
      name
      points {
        lat
        lng
      }
    }
  }
`;

const toActivityProvider = (key: keyof RecentActivities): ActivityProvider => {
  switch (key) {
    case 'garmin':
      return 'GARMIN';
    case 'suunto':
      return 'SUUNTO';
    case 'coros':
      return 'COROS';
    default: {
      const exhaustive: never = key;
      return exhaustive;
    }
  }
};

function FitTrackBounds({positions}: {positions: LatLngExpression[]}) {
  const map = useMap();
  useEffect(() => {
    const fit = () => {
      map.invalidateSize();
      if (positions.length === 0) {
        return;
      }
      if (positions.length === 1) {
        const point = positions[0] as [number, number];
        map.setView(point, 14);
        return;
      }
      map.fitBounds(L.latLngBounds(positions as [number, number][]), {padding: [28, 28], maxZoom: 16});
    };
    fit();
    const timer = window.setTimeout(fit, 250);
    return () => window.clearTimeout(timer);
  }, [map, positions]);
  return null;
}

type RecentActivities = {
  garmin: ProviderActivity[];
  suunto: ProviderActivity[];
  coros: ProviderActivity[];
};

const PROVIDERS: Array<{key: keyof RecentActivities; label: string}> = [
  {key: 'garmin', label: 'Garmin'},
  {key: 'suunto', label: 'Suunto'},
  {key: 'coros', label: 'COROS'}
];

const formatDuration = (seconds?: number | null) => {
  if (seconds == null) {
    return '—';
  }
  const hours = Math.floor(seconds / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  const rest = seconds % 60;
  if (hours > 0) {
    return `${hours}h ${minutes}m`;
  }
  if (minutes > 0) {
    return `${minutes}m ${rest}s`;
  }
  return `${rest}s`;
};

const CloseDialogButton = () => {
  const {close} = useDialogContext();
  return (
    <Button variant="contained" onClick={close}>
      Close
    </Button>
  );
};

const ActivityTrackDialog = ({
  activity,
  provider,
  userId,
  open,
  onClose
}: {
  activity: ProviderActivity | null;
  provider: ActivityProvider;
  userId: number;
  open: boolean;
  onClose: () => void;
}) => {
  const {data, loading, error} = useQuery<{item: {id: number; name?: string | null; points: TrackPoint[]}}>(MY_ACTIVITY, {
    variables: {provider, id: activity?.id ?? 0, userId},
    skip: !open || !activity || !userId
  });

  const positions = useMemo(
    () =>
      (data?.item.points || [])
        .filter((point): point is {lat: number; lng: number} => Number.isFinite(point.lat) && Number.isFinite(point.lng))
        .map((point) => [point.lat, point.lng] as LatLngExpression),
    [data]
  );

  const title = activity?.name || activity?.externalId || 'Activity';
  const start = positions[0] as [number, number] | undefined;
  const end = positions.length > 1 ? (positions[positions.length - 1] as [number, number]) : undefined;

  return (
    <CustomDialog
      dialogTitle={title}
      open={open}
      resetDialog={onClose}
      maxWidth="md"
      primaryButton={<CloseDialogButton />}
      secondaryButton={<></>}
    >
      {loading ? (
        <Box sx={{display: 'flex', justifyContent: 'center', py: 4}}>
          <CircularProgress size={28} />
        </Box>
      ) : null}
      {error ? (
        <Alert severity="error" sx={{mt: 1}}>
          {error.message}
        </Alert>
      ) : null}
      {!loading && !error && positions.length === 0 ? (
        <Typography variant="body2" color="text.secondary" sx={{py: 2}}>
          This activity has no GPS track.
        </Typography>
      ) : null}
      {!loading && !error && positions.length > 0 ? (
        <Box sx={{height: 420, width: '100%', mt: 1, borderRadius: 1, overflow: 'hidden'}}>
          <MapContainer center={start || [50.08, 14.42]} zoom={13} style={{width: '100%', height: '100%', position: 'relative'}}>
            <FitTrackBounds positions={positions} />
            <Polyline pathOptions={{color: '#1976d2', weight: 4}} positions={positions} />
            {start ? <CircleMarker center={start} radius={6} pathOptions={{color: '#2e7d32', fillColor: '#66bb6a', fillOpacity: 1, weight: 2}} /> : null}
            {end ? <CircleMarker center={end} radius={6} pathOptions={{color: '#c62828', fillColor: '#ef5350', fillOpacity: 1, weight: 2}} /> : null}
          </MapContainer>
        </Box>
      ) : null}
    </CustomDialog>
  );
};

const ActivityTable = ({
  activities,
  provider,
  userId
}: {
  activities: ProviderActivity[];
  provider: keyof RecentActivities;
  userId: number;
}) => {
  const [viewing, setViewing] = useState<ProviderActivity | null>(null);

  if (!activities.length) {
    return (
      <Typography variant="body2" color="text.secondary" sx={{mb: 2}}>
        No activities in the last 7 days.
      </Typography>
    );
  }

  return (
    <>
      <Table size="small" sx={{mb: 2}}>
        <TableHead>
          <TableRow>
            <TableCell>Name</TableCell>
            <TableCell>Type</TableCell>
            <TableCell>Started</TableCell>
            <TableCell>Duration</TableCell>
            <TableCell />
          </TableRow>
        </TableHead>
        <TableBody>
          {activities.map((activity) => (
            <TableRow key={activity.id}>
              <TableCell>
                <Typography variant="body2">{activity.name || activity.externalId || '—'}</Typography>
                {activity.externalId ? (
                  <Typography variant="caption" color="text.secondary">
                    {activity.externalId}
                  </Typography>
                ) : null}
              </TableCell>
              <TableCell>{activity.activityType || '—'}</TableCell>
              <TableCell>{dayjs.unix(activity.startTimeInSeconds).format('D MMM YYYY HH:mm')}</TableCell>
              <TableCell>{formatDuration(activity.durationInSeconds)}</TableCell>
              <TableCell align="right">
                <Button
                  size="small"
                  variant="outlined"
                  onClick={(event) => {
                    event.stopPropagation();
                    setViewing(activity);
                  }}
                >
                  View
                </Button>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
      <DialogContextProvider>
        <ActivityTrackDialog
          activity={viewing}
          provider={toActivityProvider(provider)}
          userId={userId}
          open={viewing != null}
          onClose={() => {
            if (!viewing) {
              return;
            }
            setViewing(null);
          }}
        />
      </DialogContextProvider>
    </>
  );
};

const RecentActivitiesContent = ({userId, enabled}: {userId: number; enabled: boolean}) => {
  const {data, loading, error} = useQuery<{userRecentActivities: RecentActivities}>(getUserRecentActivities, {
    variables: {userId},
    skip: !enabled || !userId
  });

  if (loading) {
    return (
      <Box sx={{display: 'flex', justifyContent: 'center', py: 4}}>
        <CircularProgress size={28} />
      </Box>
    );
  }

  if (error) {
    return (
      <Typography color="error" variant="body2">
        {error.message}
      </Typography>
    );
  }

  const activities = data?.userRecentActivities;

  return (
    <>
      {PROVIDERS.map((provider) => (
        <Box key={provider.key}>
          <Typography variant="subtitle1" sx={{mb: 1}}>
            {provider.label}
            {activities ? ` (${activities[provider.key].length})` : ''}
          </Typography>
          <ActivityTable activities={activities?.[provider.key] || []} provider={provider.key} userId={userId} />
        </Box>
      ))}
    </>
  );
};

const RecentActivitiesMenuItem = (props: ActionMenuItemProps & {onClose?: () => void}) => {
  const {onClose, ...rest} = props;
  const {id, record} = useRecordContext();
  const [open, setOpen] = useState(false);
  const userName = record?.name || record?.email || `User #${id}`;

  const handleOpen: React.MouseEventHandler = (event) => {
    setOpen(true);
    event.stopPropagation();
  };

  const handleClose = () => {
    if (!open) {
      return;
    }
    setOpen(false);
    if (typeof onClose === 'function') {
      onClose();
    }
  };

  return (
    <>
      <ActionMenuItem {...rest} label="View last activities" icon={<History />} onClick={handleOpen} />
      <DialogContextProvider>
        <CustomDialog
          dialogTitle={`Last 7 days activities: ${userName}`}
          open={open}
          resetDialog={handleClose}
          maxWidth="md"
          primaryButton={<CloseDialogButton />}
          secondaryButton={<></>}
        >
          <RecentActivitiesContent userId={Number(id)} enabled={open} />
        </CustomDialog>
      </DialogContextProvider>
    </>
  );
};

export default RecentActivitiesMenuItem;
